<template>
  <v-dialog
    v-model="dialog"
    max-width="600px"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-icon
        small
        class="mr-2"
        v-bind="attrs"
        v-on="on"
      >
        mdi-pencil
      </v-icon>
    </template>
    <v-card>
      <v-card-title>
        <span class="text-h5">
          Set Layer Properties
        </span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-form
            ref="appUserForm"
            v-model="valid"
            lazy-validation
          >
            <v-row>
              <v-text-field
                v-model="newName"
                label="Layer Name"
              />
            </v-row>
            <v-row>
              <v-text-field
                v-model="newUdm"
                label="Layer UDM"
              />
            </v-row>
            <v-row>
              <v-text-field
                v-model="newColor"
                :rules="code_rules"
                label="Layer Color"
              />
              <div
                id="layerColor"
                class="ml-3 my-auto"
                :style="`background-color: ${newColor}`"
              />
            </v-row>
            <v-row>
              <v-slider
                v-model="newOpacity"
                label="Layer Opacity"
                class="align-center"
                color="color_green"
                track-color="grey"
                min="0"
                max="100"
                hide-details
              >
                <template v-slot:append>
                  <v-text-field
                    v-model="newOpacity"
                    class="mt-0 pt-0"
                    hide-details
                    single-line
                    type="number"
                    style="width: 60px"
                  />
                </template>
              </v-slider>
            </v-row>
            <v-row class="mt-4">
              <v-checkbox
                v-model="isAggregated"
                label="Enable Data Aggregation"
                color="color_green"
              />
            </v-row>

            <v-row
              v-if="isAggregated"
              class="align-center"
            >
              <v-col cols="8">
                <div class="mb-2">
                  Aggregation Type *
                </div>
                <v-chip-group
                  v-model="aggregationType"
                  :rules="aggregationTypeRules"
                  mandatory
                  active-class="color_green--text"
                  row
                  class="mb-4"
                >
                  <v-chip
                    v-for="type in aggregationTypes"
                    :key="type"
                    :value="type"
                    outlined
                  >
                    {{ type }}
                  </v-chip>
                </v-chip-group>
              </v-col>
              <v-col cols="4">
                <div class="mb-2">
                  Aggregation Value *
                </div>
                <v-text-field
                  v-model.number="aggregationValue"
                  :rules="aggregationValueRules"
                  type="number"
                  min="1"
                  style="width: 100px;"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="blue darken-1"
          text
          @click="close()"
        >
          Cancel
        </v-btn>
        <v-btn
          color="blue darken-1"
          text
          :disabled="!isSaveEnabled"
          @click="save()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { getRegExp } from '@/formRules.js';

export default {
  props: {
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      dialog: false,
      valid: true,
      newColor: this.item.layer_color,
      newName: this.item.layer_label,
      newUdm: this.item.udm,
      newOpacity: this.item.layer_opacity,
      code_rules: getRegExp('hex_code'),
      isAggregated: !!this.item.aggregation_interval,
      aggregationValue: this.item.aggregation_interval?.split(' ')[0] ?? null,
      aggregationType: this.item.aggregation_interval?.split(' ')[1] ?? null,
      aggregationTypes: ['minutes', 'hours', 'days', 'months'],
      aggregationTypeRules: [
        v => (!this.isAggregated || !!v) || 'Aggregation type is required'
      ],
      aggregationValueRules: [
        v => (!this.isAggregated || !!v) || 'Aggregation value is required'
      ]
    };
  },
  computed: {
    ...mapState([]),
    isSaveEnabled() {
      if (!this.valid) {
        return false;
      }

      if (!this.isAggregated) {
        return true;
      }

      const hasType = !!this.aggregationType;
      const hasValue = this.aggregationValue !== null && this.aggregationValue !== '' && Number(this.aggregationValue) > 0;

      return hasType && hasValue;
    }
  },
  watch: {
    isAggregated(newVal) {
      if (newVal && !this.aggregationType) {
        this.aggregationType = 'hours';
      }

      if (!newVal) {
        this.aggregationType = null;
        this.aggregationValue = null;
      }
    }
  },
  methods: {
    save() {
      if (!this.$refs.appUserForm.validate()) {
        return;
      }

      if (this.newColor.length > 0) {
        this.item.layer_color = this.newColor;
      } else {
        this.newColor = this.item.layer_color;
      }

      this.item.layer_label = this.newName;
      this.item.layer_opacity = this.newOpacity;
      this.item.udm = this.newUdm;

      this.item.aggregation_interval = this.isAggregated
        ? `${this.aggregationValue} ${this.aggregationType}`
        : null;

      this.dialog = false;
    },
    close() {
      this.newColor = this.item.layer_color;
      this.newOpacity = this.item.layer_opacity;
      this.newName = this.item.layer_label;
      this.newUdm = this.item.udm;

      this.isAggregated = !!this.item.aggregation_interval;
      this.aggregationValue = this.item.aggregation_interval?.split(' ')[0] ?? null;
      this.aggregationType = this.item.aggregation_interval?.split(' ')[1] ?? null;

      this.dialog = false;
    }
  }
};
</script>
